import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  HouseSimple, BellRinging, ChatCircleText,
  CheckCircle, ArrowRight, X,
} from "@phosphor-icons/react";
import { isSupabaseConfigured } from "@/lib/supabase";
import { hasSentMessage } from "@/lib/messaging";
import useInstallPrompt from "@/lib/hooks/useInstallPrompt";
import usePushState from "@/lib/hooks/usePushState";

/**
 * Parent · "first 7 days" onboarding strip.
 *
 * Three soft, calm tasks that quietly self-complete as the underlying
 * state flips:
 *   1. Install the app — auto-completes when the PWA is in standalone
 *      mode (after Add to Home Screen on iOS, or beforeinstallprompt
 *      acceptance on Android/Chrome). On iOS we show a one-line hint
 *      because Safari doesn't fire the prompt.
 *   2. Turn on message notifications — auto-completes once
 *      `subscribeToPush()` reports `subscribed: true`.
 *   3. Send a hello to the teacher — auto-completes once the parent has
 *      actually sent a message.
 *
 * Whole strip:
 *   * shows only after we've finished the initial state probe (no flash
 *     of a half-empty checklist on cold load),
 *   * hides itself once all three are complete OR the parent dismisses
 *     it (dismissal is persisted per-profile via localStorage),
 *   * uses calm primary tones, never red. It's a welcome, not a nag.
 */
export default function ParentOnboardingStrip({ profile }) {
  const profileId = profile?.id;
  const dismissKey = profileId ? `parent_onboarding_dismissed:${profileId}` : null;
  const installAckKey = profileId ? `parent_onboarding_install_ack:${profileId}` : null;
  const [dismissed, setDismissed] = useState(false);
  const [installAck, setInstallAck] = useState(false);
  const [hasMessaged, setHasMessaged] = useState(null); // null = unknown / loading

  const { isInstallable, promptInstall, isIOS, isStandalone } = useInstallPrompt();
  const push = usePushState();

  // Hydrate dismissal flag once we know the profile.
  useEffect(() => {
    if (!dismissKey) return;
    try {
      setDismissed(window.localStorage.getItem(dismissKey) === "1");
      setInstallAck(window.localStorage.getItem(installAckKey) === "1");
    } catch {
      /* ignore */
    }
  }, [dismissKey, installAckKey]);

  // Detect whether the parent has sent a message. The data layer uses the
  // live count in production and a send-only marker in demo mode so seeded
  // preview messages do not falsely complete first-week onboarding.
  useEffect(() => {
    let alive = true;
    if (!profileId) {
      setHasMessaged(false);
      return undefined;
    }
    (async () => {
      try {
        const sent = await hasSentMessage(profileId);
        if (alive) setHasMessaged(sent);
      } catch {
        if (alive) setHasMessaged(false);
      }
    })();
    return () => { alive = false; };
  }, [profileId]);

  const installDone = isStandalone || installAck;
  const pushDone = push.state?.subscribed;
  const messageDone = hasMessaged;

  const allKnown =
    !push.loading && hasMessaged !== null;
  const allDone = installDone && pushDone && messageDone;

  // Tasks list — `done` is read live; `cta` mounts behaviour; `hint` is a
  // single-line caption shown under the row when we know we can't do it
  // automatically (e.g. iOS install).
  const installCanPrompt = isInstallable && !installDone;
  const tasks = useMemo(() => [
    {
      key: "install",
      icon: HouseSimple,
      title: "Install the app",
      blurb: isIOS
        ? "Tap the Share icon in Safari, then “Add to Home Screen” to keep us one tap away."
        : installCanPrompt
          ? "Pin us to your home screen so you can open updates with one tap."
          : "On this browser, use the menu (⋮ or share icon) and choose “Install” or “Add to Home Screen”.",
      done: installDone,
      ctaLabel: isIOS
        ? "Got it"
        : installCanPrompt
          ? "Install now"
          : "Got it",
      onAction: () => {
        if (installCanPrompt) {
          promptInstall();
          return;
        }
        // Manual confirmation — once a user taps "Got it" on iOS or a
        // browser that won't fire beforeinstallprompt, persist that
        // acknowledgement so the strip can complete.
        setInstallAck(true);
        try {
          if (installAckKey) window.localStorage.setItem(installAckKey, "1");
        } catch {
          /* ignore */
        }
      },
      ctaDisabled: false,
    },
    {
      key: "push",
      icon: BellRinging,
      title: "Turn on message notifications",
      blurb: "Get a heads-up when your teacher or center sends a message.",
      done: !!pushDone,
      error: push.error
        ? isSupabaseConfigured
          ? "We couldn't enable message notifications right now. Try again later in Preferences."
          : "Message notifications are available in the live app."
        : null,
      ctaLabel: push.busy
        ? "Enabling…"
        : push.state?.supported === false
          ? "Not supported on this browser"
          : "Enable",
      onAction: () => {
        if (push.state?.supported && !pushDone) push.enable().catch(() => {});
      },
      ctaDisabled: push.busy || push.state?.supported === false,
    },
    {
      key: "say-hi",
      icon: ChatCircleText,
      title: "Send a hello to your teacher",
      blurb: "Open Messages and send a quick “Hi.”",
      done: !!messageDone,
      ctaLabel: "Open messages",
      to: "/parent/messages",
    },
  ], [isIOS, installAckKey, installCanPrompt, installDone, push, pushDone, messageDone, promptInstall]);

  const completedCount = tasks.filter((t) => t.done).length;

  if (!allKnown || dismissed || allDone) return null;

  const dismiss = () => {
    setDismissed(true);
    try { if (dismissKey) window.localStorage.setItem(dismissKey, "1"); } catch { /* ignore */ }
  };

  return (
    <article
      data-testid="parent-onboarding-strip"
      data-completed={completedCount}
      className="relative rounded-2xl border border-primary/20 bg-primary/5 p-4 sm:p-5"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss welcome checklist"
        data-testid="parent-onboarding-strip-dismiss"
        className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full text-neutral-500 hover:bg-white"
      >
        <X size={12} weight="bold" />
      </button>
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary-hover">
        Welcome
      </p>
      <p className="mt-1 font-heading text-base font-semibold tracking-tight text-neutral-900">
        Get set up in your first week
      </p>
      <p className="mt-1 text-sm leading-relaxed text-neutral-600">
        Three small steps to stay connected.
      </p>
      <ol className="mt-3 space-y-1.5">
        {tasks.map((t) => (
          <OnboardingTask key={t.key} task={t} />
        ))}
      </ol>
      <p className="mt-2 text-[11px] text-neutral-500">
        {completedCount} of {tasks.length} done
      </p>
    </article>
  );
}

function OnboardingTask({ task }) {
  const Icon = task.done ? CheckCircle : task.icon;
  return (
    <li
      data-testid={`parent-onboarding-task-${task.key}`}
      data-done={task.done}
      className={`grid grid-cols-[2rem_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-2xl border bg-white p-3 transition-all ${
        task.done ? "border-primary/30" : "border-neutral-200"
      }`}
    >
      <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
        task.done ? "bg-primary/10 text-primary-hover" : "bg-neutral-100 text-neutral-500"
      }`}>
        <Icon size={14} weight={task.done ? "fill" : "duotone"} />
      </span>
      <div className="min-w-0">
        <p className="font-heading text-sm font-semibold text-neutral-900">
          {task.title}
        </p>
        <p className="mt-0.5 text-[12px] leading-relaxed text-neutral-600">
          {task.blurb}
        </p>
        {task.error && (
          <p role="alert" className="mt-1 text-xs leading-relaxed text-neutral-500">
            {task.error}
          </p>
        )}
        <div className="mt-2">
          {task.done ? (
            <span className="inline-flex h-9 items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-3 text-[11px] font-bold uppercase tracking-wider text-primary-hover">
              Done
            </span>
          ) : task.to ? (
            <Link
              to={task.to}
              data-testid={`parent-onboarding-task-${task.key}-cta`}
              onClick={task.onAction}
              className="inline-flex h-11 items-center gap-1 rounded-full bg-primary px-3 text-xs font-semibold text-white shadow-sm hover:bg-primary-hover"
            >
              {task.ctaLabel}
              <ArrowRight size={11} weight="bold" />
            </Link>
          ) : (
            <button
              type="button"
              data-testid={`parent-onboarding-task-${task.key}-cta`}
              onClick={task.onAction}
              disabled={task.ctaDisabled}
              className="inline-flex h-11 items-center gap-1 rounded-full bg-primary px-3 text-xs font-semibold text-white shadow-sm hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              {task.ctaLabel}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
